/**
 * Dark-theme blog section for the LandingPage.
 * Shows the 3 most recent posts from "Vita da Host" blog (RSS feed).
 * Matches the zinc-900 section aesthetic of the landing.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { ArrowUpRight, Newspaper, CaretRight } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function FadeIn({ children, delay = 0 }) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setVisible(true), delay);
    return () => clearTimeout(t);
  }, [delay]);
  return (
    <div
      className={`transition-all duration-700 ease-out ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
      }`}
    >
      {children}
    </div>
  );
}

function formatRelative(isoish) {
  if (!isoish) return '';
  const d = new Date(isoish);
  if (isNaN(d)) return '';
  const diffDays = Math.floor((Date.now() - d.getTime()) / 86400000);
  if (diffDays <= 0) return 'Oggi';
  if (diffDays === 1) return 'Ieri';
  if (diffDays < 7) return `${diffDays} giorni fa`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} settimane fa`;
  if (diffDays < 365) return `${Math.floor(diffDays / 30)} mesi fa`;
  return `${Math.floor(diffDays / 365)} anni fa`;
}

export default function LandingBlogSection() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    axios
      .get(`${API}/news/blog-feed`)
      .then((r) => mounted && setItems((r.data?.items || []).slice(0, 3)))
      .catch(() => { /* silent */ })
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, []);

  // Hide section entirely if no posts and not loading
  if (!loading && items.length === 0) return null;

  return (
    <section className="py-12 sm:py-20 bg-zinc-900 text-white px-4 sm:px-6" data-testid="landing-blog-section">
      <div className="max-w-6xl mx-auto">
        <FadeIn>
          <div className="flex items-center gap-2 mb-3">
            <Newspaper size={14} weight="fill" className="text-emerald-400" />
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-400">Dal blog Vita da Host</p>
          </div>
          <h2 className="text-2xl sm:text-4xl font-black tracking-tight mb-2">
            Ultime notizie dal nostro blog
          </h2>
          <p className="text-sm sm:text-base text-zinc-400 max-w-2xl mb-10 leading-relaxed">
            Rimani informato sulle novità del mondo degli affitti brevi: normativa, fiscalità, trend di mercato
            e storie di host come te.
          </p>
        </FadeIn>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-5">
          {loading
            ? [0, 1, 2].map((i) => (
                <div
                  key={i}
                  className="bg-zinc-800 border-l-2 border-zinc-700 h-64 animate-pulse"
                />
              ))
            : items.map((it, i) => (
                <FadeIn key={it.link} delay={i * 150}>
                  <a
                    href={it.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group block bg-zinc-800 border-l-2 border-emerald-400 h-full overflow-hidden transition-all duration-500 hover:border-emerald-300 hover:bg-zinc-800/80 hover:-translate-y-1"
                    data-testid={`landing-blog-card-${i}`}
                  >
                    {/* Image */}
                    {it.image && (
                      <div className="relative aspect-[16/9] overflow-hidden bg-zinc-700">
                        <img
                          src={it.image}
                          alt=""
                          loading="lazy"
                          className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-zinc-900/80 via-zinc-900/10 to-transparent" />
                        {it.published && (
                          <div className="absolute top-3 left-3 bg-zinc-900/80 backdrop-blur px-2 py-1 text-[10px] font-black uppercase tracking-wider text-emerald-300">
                            {formatRelative(it.published)}
                          </div>
                        )}
                        <div className="absolute top-3 right-3 w-7 h-7 bg-emerald-500 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity duration-300">
                          <ArrowUpRight size={13} weight="bold" className="text-zinc-900" />
                        </div>
                      </div>
                    )}

                    {/* Body */}
                    <div className="p-5 sm:p-6">
                      {!it.image && it.published && (
                        <div className="flex items-center gap-2 mb-3">
                          <span className="text-[10px] font-bold uppercase tracking-widest text-emerald-400">
                            {formatRelative(it.published)}
                          </span>
                        </div>
                      )}
                      <h3 className="font-black text-lg mb-2 leading-tight text-white line-clamp-2 group-hover:text-emerald-200 transition-colors">
                        {it.title}
                      </h3>
                      <p className="text-xs text-zinc-400 leading-relaxed line-clamp-3 mb-4">
                        {it.excerpt}
                      </p>
                      <div className="inline-flex items-center gap-1.5 text-[11px] font-black uppercase tracking-[0.18em] text-emerald-400 group-hover:text-emerald-300 transition-colors">
                        Leggi articolo
                        <CaretRight
                          size={11}
                          weight="bold"
                          className="transition-transform duration-300 group-hover:translate-x-1"
                        />
                      </div>
                    </div>
                  </a>
                </FadeIn>
              ))}
        </div>

        {/* Footer link */}
        <FadeIn delay={500}>
          <div className="mt-8 sm:mt-10 text-center">
            <a
              href="https://vienidanoi.it/blog"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-zinc-300 hover:text-white transition-colors border-b border-zinc-700 hover:border-emerald-400 pb-1"
              data-testid="landing-blog-all-link"
            >
              Tutti gli articoli sul blog
              <ArrowUpRight size={14} weight="bold" />
            </a>
          </div>
        </FadeIn>
      </div>
    </section>
  );
}
